import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, CheckIcon, ClockIcon, PlayIcon, SpeakerWaveIcon } from '@heroicons/react/24/outline';
import GameSetupShell, { RunHeader, StatGrid, startButtonClass, useGameStats } from '../../components/Games2/GameSetupShell';
import { Eyebrow, INPUT, Pill, Segmented } from '../../components/UI/AppUI';

const PREDEFINED_TASKS = [
  { id: 1, title: 'Constant HDG only', compass: 'constant', altimeter: 'off', airspeed: false, turbulence: true, audio: false },
  { id: 2, title: 'Constant ALT only', compass: 'off', altimeter: 'constant', airspeed: false, turbulence: true, audio: false },
  { id: 3, title: 'Constant HDG/ALT', compass: 'constant', altimeter: 'constant', airspeed: false, turbulence: true, audio: false },
  { id: 4, title: 'Changing HDG only', compass: 'changing', altimeter: 'off', airspeed: false, turbulence: true, audio: false },
  { id: 5, title: 'Changing HDG, Constant ALT', compass: 'changing', altimeter: 'constant', airspeed: false, turbulence: true, audio: false },
  { id: 6, title: 'Changing HDG & ALT', compass: 'changing', altimeter: 'changing', airspeed: false, turbulence: true, audio: false },
  { id: 7, title: 'Constant HDG, Changing ALT/SPD', compass: 'constant', altimeter: 'changing', airspeed: true, turbulence: true, audio: false },
  { id: 8, title: 'Changing HDG/SPD, Constant ALT', compass: 'changing', altimeter: 'constant', airspeed: true, turbulence: true, audio: false },
  { id: 9, title: 'Changing HDG/ALT/SPD', compass: 'changing', altimeter: 'changing', airspeed: true, turbulence: true, audio: false },
  { id: 10, title: 'Constant HDG, Changing ALT/SPD W/Audio', compass: 'constant', altimeter: 'changing', airspeed: true, turbulence: true, audio: true },
  { id: 11, title: 'Changing HDG/SPD, Constant ALT W/Audio', compass: 'changing', altimeter: 'constant', airspeed: true, turbulence: true, audio: true },
  { id: 12, title: 'Changing HDG/ALT/SPD W/Audio', compass: 'changing', altimeter: 'changing', airspeed: true, turbulence: true, audio: true },
];

const TASK_GROUPS = [
  { title: 'Basic controls', tasks: PREDEFINED_TASKS.slice(0, 3) },
  { title: 'Climb and turn', tasks: PREDEFINED_TASKS.slice(3, 6) },
  { title: 'Include air speed', tasks: PREDEFINED_TASKS.slice(6, 9) },
  { title: 'Include audible task', tasks: PREDEFINED_TASKS.slice(9, 12) },
];

const MODES = [{ value: 'off', label: 'Off' }, { value: 'constant', label: 'Constant' }, { value: 'changing', label: 'Changing' }];

const score = (r) => parseFloat(r.final_score) || 0;
const pct = (v) => `${v != null ? parseFloat(v).toFixed(0) : '0'}%`;

const Switch = ({ label, checked, onChange }) => (
  <button type="button" role="switch" aria-checked={checked} onClick={() => onChange(!checked)} className="flex w-full items-center justify-between py-1.5 text-sm text-slate-700 dark:text-gray-300">
    {label}
    <span className={`relative h-5 w-9 rounded-full transition-colors ${checked ? 'bg-primary-600' : 'bg-slate-300 dark:bg-white/15'}`}>
      <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-4' : 'translate-x-0.5'}`} />
    </span>
  </button>
);

const SettingRow = ({ label, children }) => (
  <div className="flex flex-wrap items-center justify-between gap-2">
    <span className="text-sm text-slate-700 dark:text-gray-300">{label}</span>
    {children}
  </div>
);

// Heading, altitude and speed deviation across the run (each clamped to its own range)
const DeviationGraph = ({ deviationData }) => {
  const data = Array.isArray(deviationData) ? [...deviationData].sort((a, b) => new Date(a.timestamp || 0) - new Date(b.timestamp || 0)) : [];
  if (data.length === 0) {
    return <p className="rounded-lg border border-dashed border-slate-300 py-6 text-center text-xs text-slate-500 dark:border-white/10 dark:text-gray-500">No deviation data for this run</p>;
  }
  const H = 130;
  const mid = H / 2;
  const line = (key, max) => data.map((d, i) => {
    const v = Math.max(-max, Math.min(max, d[key] || 0)) / max;
    return `${(i / Math.max(data.length - 1, 1)) * 100},${mid - v * (mid - 8)}`;
  }).join(' ');
  return (
    <div className="overflow-hidden rounded-lg border border-slate-200 bg-slate-50 dark:border-white/10 dark:bg-white/[0.03]">
      <svg width="100%" height={H} viewBox={`0 0 100 ${H}`} preserveAspectRatio="none" className="text-slate-400">
        <line x1={0} x2={100} y1={mid} y2={mid} stroke="currentColor" strokeOpacity="0.3" strokeDasharray="2,2" vectorEffect="non-scaling-stroke" />
        {data.length > 1 && (
          <>
            <polyline points={line('headingDeviation', 90)} fill="none" stroke="#3B82F6" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
            <polyline points={line('altitudeDeviation', 250)} fill="none" stroke="#EF4444" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
            <polyline points={line('speedDeviation', 30)} fill="none" stroke="#22C55E" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
          </>
        )}
      </svg>
    </div>
  );
};

const MICSetup2 = () => {
  const { subjectId } = useParams();
  const navigate = useNavigate();
  const game = useGameStats('MIC', subjectId);
  const { stats } = game;
  const [realTestMode, setRealTestMode] = useState(false);
  const [activeMode, setActiveMode] = useState('predefined');
  const [selectedTask, setSelectedTask] = useState(null);
  const [custom, setCustom] = useState({ duration: 3, compass: 'changing', altimeter: 'changing', airspeed: true, turbulence: true, audio: true });

  const startGame = (settings) => navigate(`/games/mic/${subjectId}/play`, { state: { settings } });

  const handleStart = () => {
    if (realTestMode) {
      startGame({ runMode: 'real_test', duration: 1080, tasks: PREDEFINED_TASKS });
    } else if (activeMode === 'predefined' && selectedTask) {
      startGame({ runMode: 'predefined', predefinedTaskNumber: selectedTask.id, duration: 180, ...selectedTask });
    } else if (activeMode === 'custom') {
      startGame({ runMode: 'custom', ...custom, duration: custom.duration * 60 });
    }
  };

  const canStart = realTestMode || (activeMode === 'predefined' && selectedTask) || activeMode === 'custom';

  const picker = realTestMode ? (
    <>
      <button onClick={() => setRealTestMode(false)} className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-900 dark:text-gray-400 dark:hover:text-white">
        <ArrowLeftIcon className="h-4 w-4" />Back to practice
      </button>
      <div className="mt-4 rounded-xl border border-rose-500/30 bg-rose-500/10 p-4">
        <Eyebrow className="!text-rose-600 dark:!text-rose-300">Real test mode</Eyebrow>
        <p className="mt-2 text-lg font-semibold text-slate-900 dark:text-white">All 12 tasks, back to back</p>
        <ul className="mt-3 space-y-1 text-sm text-slate-600 dark:text-gray-300">
          <li>18 minutes in total</li>
          <li>1.5 minutes per task with a 10 second briefing</li>
          <li>Settings are locked during the test</li>
        </ul>
      </div>
      <button onClick={handleStart} className={`${startButtonClass('rose')} mt-4`}><PlayIcon className="h-5 w-5" />Start real test</button>
    </>
  ) : (
    <>
      <button onClick={() => setRealTestMode(true)} className="group flex w-full items-center gap-3 rounded-xl border border-rose-500/30 bg-rose-500/10 p-3 text-left transition-colors hover:bg-rose-500/15">
        <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-rose-600 text-white"><ClockIcon className="h-5 w-5" /></span>
        <span className="min-w-0 flex-1">
          <span className="block font-semibold text-slate-900 dark:text-white">Real test mode</span>
          <span className="block text-xs text-slate-500 dark:text-gray-400">12 tasks · 18 minutes · exam conditions</span>
        </span>
      </button>

      <div className="my-4 flex items-center gap-3 text-xs font-semibold uppercase tracking-widest text-slate-400 dark:text-gray-500">
        <span className="h-px flex-1 bg-slate-200 dark:bg-white/10" />or practise<span className="h-px flex-1 bg-slate-200 dark:bg-white/10" />
      </div>

      <Segmented label="Practice type" size="md" value={activeMode} onChange={setActiveMode} options={[{ value: 'predefined', label: 'Pre-defined tasks' }, { value: 'custom', label: 'Custom' }]} />

      {activeMode === 'predefined' ? (
        <div role="radiogroup" aria-label="Task" className="mt-4 max-h-[420px] space-y-4 overflow-y-auto pr-1 lg:max-h-[calc(100vh-420px)]">
          {TASK_GROUPS.map((g) => (
            <div key={g.title}>
              <p className="mb-1.5 text-xs font-semibold uppercase tracking-widest text-slate-500 dark:text-gray-500">{g.title}</p>
              <div className="space-y-1">
                {g.tasks.map((t) => {
                  const active = selectedTask?.id === t.id;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      onClick={() => setSelectedTask(t)}
                      className={`flex w-full items-center gap-3 rounded-lg border px-3 py-2 text-left text-sm transition-colors ${active
                        ? 'border-transparent bg-primary-600 text-white'
                        : 'border-slate-200 text-slate-700 hover:bg-slate-50 dark:border-white/10 dark:text-gray-200 dark:hover:bg-white/[0.05]'}`}
                    >
                      <span className={`w-5 flex-shrink-0 text-xs font-semibold ${active ? 'text-primary-100' : 'text-slate-400'}`}>{t.id}</span>
                      <span className="min-w-0 flex-1">{t.title}</span>
                      {t.audio && <SpeakerWaveIcon className={`h-4 w-4 flex-shrink-0 ${active ? 'text-white' : 'text-primary-500'}`} aria-label="Includes audio" />}
                      {active && <CheckIcon className="h-4 w-4 flex-shrink-0" />}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="mt-4 space-y-3">
          <SettingRow label="Duration (minutes)">
            <input type="number" min="1" max="10" value={custom.duration} onChange={(e) => setCustom({ ...custom, duration: parseInt(e.target.value, 10) || 3 })} className={`${INPUT} !w-20 !py-1.5`} />
          </SettingRow>
          <SettingRow label="Heading"><Segmented label="Heading" value={custom.compass} onChange={(v) => setCustom({ ...custom, compass: v })} options={MODES} /></SettingRow>
          <SettingRow label="Altitude"><Segmented label="Altitude" value={custom.altimeter} onChange={(v) => setCustom({ ...custom, altimeter: v })} options={MODES} /></SettingRow>
          <SettingRow label="Airspeed"><Segmented label="Airspeed" value={custom.airspeed ? 'on' : 'off'} onChange={(v) => setCustom({ ...custom, airspeed: v === 'on' })} options={[{ value: 'off', label: 'Off' }, { value: 'on', label: 'On' }]} /></SettingRow>
          <div className="border-t border-slate-200 pt-2 dark:border-white/10">
            <Switch label="Turbulence" checked={custom.turbulence} onChange={(v) => setCustom({ ...custom, turbulence: v })} />
            <Switch label="Auditory task" checked={custom.audio} onChange={(v) => setCustom({ ...custom, audio: v })} />
          </div>
        </div>
      )}

      <button onClick={handleStart} disabled={!canStart} className={`${startButtonClass()} mt-4`}>
        <PlayIcon className="h-5 w-5" />{activeMode === 'predefined' && !selectedTask ? 'Pick a task to start' : 'Start'}
      </button>
    </>
  );

  return (
    <GameSetupShell
      code="MIC"
      name="Monitoring & Instruments Coordination"
      description="Psychomotor aptitude and multi-tasking assessment game"
      game={game}
      heroStats={[
        { label: 'Runs', value: stats?.total_runs || 0 },
        { label: 'Best', value: `${stats?.best_score?.toFixed(0) || 0}%` },
        { label: 'Average', value: `${stats?.average_score?.toFixed(0) || 0}%` },
      ]}
      picker={picker}
      runs={stats?.score_history || []}
      chart={{ series: [{ label: 'Final score', color: '#3B82F6', value: score, main: true }], max: 100, suffix: '%' }}
      renderRun={(run) => (
        <div className="space-y-4">
          <RunHeader
            run={run}
            level={run.level}
            sub={`${new Date(run.completed_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}${run.duration ? ` · ${Math.floor(run.duration / 60)}:${String(run.duration % 60).padStart(2, '0')}` : ''}`}
            score={`${score(run).toFixed(1)}%`}
            scoreLabel="Final score"
          />
          <StatGrid
            items={[
              { label: 'Compass', value: pct(run.heading_score), sub: run.compass_mode || '–', className: 'text-blue-600 dark:text-blue-400' },
              { label: 'Altimeter', value: pct(run.altitude_score), sub: run.altimeter_mode || '–', className: 'text-rose-600 dark:text-rose-400' },
              { label: 'Airspeed', value: pct(run.speed_score), sub: run.airspeed_enabled ? 'Active' : 'Off', className: 'text-emerald-600 dark:text-emerald-400' },
              { label: 'Auditory', value: pct(run.auditory_score), sub: run.auditory_task_enabled ? `${run.auditory_correct || 0}/${run.auditory_total_expected || 0}` : 'Off', className: 'text-purple-600 dark:text-purple-400' },
            ]}
          />
          <div className="flex flex-wrap gap-1.5">
            <Pill tone={run.run_mode === 'real_test' ? 'red' : run.run_mode === 'predefined' ? 'blue' : 'gray'}>
              {run.run_mode === 'real_test' ? 'Real test mode' : run.run_mode === 'predefined' ? `Task ${run.predefined_task_number || '?'}` : 'Custom'}
            </Pill>
            {run.run_mode === 'predefined' && run.predefined_task_number && (
              <Pill>{PREDEFINED_TASKS.find((t) => t.id === run.predefined_task_number)?.title || `Task ${run.predefined_task_number}`}</Pill>
            )}
            {run.turbulence_enabled && <Pill tone="amber">Turbulence</Pill>}
          </div>
          <div>
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 dark:text-gray-500">Deviation over time</p>
              <div className="flex gap-3 text-xs text-slate-500 dark:text-gray-400">
                <span className="inline-flex items-center gap-1"><span className="h-0.5 w-3 bg-blue-500" />HDG</span>
                <span className="inline-flex items-center gap-1"><span className="h-0.5 w-3 bg-red-500" />ALT</span>
                <span className="inline-flex items-center gap-1"><span className="h-0.5 w-3 bg-green-500" />SPD</span>
              </div>
            </div>
            <DeviationGraph deviationData={run.deviation_data} />
          </div>
        </div>
      )}
    />
  );
};

export default MICSetup2;
